/**
 * Circuit Breaker Types
 *
 * This module contains type definitions for the circuit breaker pattern implementation.
 * The circuit breaker helps prevent cascading failures by tracking endpoint health
 * and automatically blocking requests to failing services.
 *
 * @module types/api/circuit-breaker
 *
 * @example
 * ```typescript
 * import type {
 *   CircuitBreaker,
 *   CircuitBreakerConfig,
 *   CircuitBreakerState,
 *   CircuitBreakerStats
 * } from '../types/api';
 * import { CircuitState, DEFAULT_CIRCUIT_BREAKER_CONFIG } from '../types/api';
 * ```
 */

import type { CoreNotificationEvent } from './notification.types';

// =============================================================================
// CIRCUIT STATE ENUM
// =============================================================================

/**
 * Enumeration of possible circuit breaker states.
 *
 * The circuit breaker operates in three states:
 * - CLOSED: Normal operation, all requests allowed
 * - OPEN: Service is failing, requests are blocked
 * - HALF_OPEN: Testing if service has recovered
 *
 * @example
 * ```typescript
 * import { CircuitState } from '../../types/api';
 *
 * function handleCircuitState(state: CircuitState): void {
 *   switch (state) {
 *     case CircuitState.CLOSED:
 *       console.log('Circuit is healthy, requests allowed');
 *       break;
 *     case CircuitState.OPEN:
 *       console.log('Circuit is open, requests blocked');
 *       break;
 *     case CircuitState.HALF_OPEN:
 *       console.log('Circuit is testing recovery');
 *       break;
 *   }
 * }
 * ```
 */
export enum CircuitState {
  /** Normal operation - all requests are allowed through */
  CLOSED = 'closed',
  /** Service is failing - requests are blocked to prevent cascading failures */
  OPEN = 'open',
  /** Testing recovery - limited requests allowed to check if service has recovered */
  HALF_OPEN = 'half_open',
}

// =============================================================================
// CIRCUIT BREAKER STATE INTERFACE
// =============================================================================

/**
 * Represents the current state of a circuit breaker for an endpoint.
 *
 * This interface tracks all the state information needed to make circuit
 * breaker decisions, including failure counts, timing, and current state.
 *
 * @example
 * ```typescript
 * import type { CircuitBreakerState } from '../../types/api';
 * import { CircuitState } from '../../types/api';
 *
 * const state: CircuitBreakerState = {
 *   endpoint: '/api/users',
 *   state: CircuitState.CLOSED,
 *   failureCount: 0,
 *   successCount: 5,
 * };
 * ```
 */
export interface CircuitBreakerState {
  /** The endpoint this circuit breaker is tracking */
  endpoint: string;

  /** Current state of the circuit breaker */
  state: CircuitState;

  /** Number of consecutive failures recorded */
  failureCount: number;

  /** Number of consecutive successes recorded (used in half-open state) */
  successCount: number;

  /** Timestamp of the last failure (Unix timestamp in milliseconds) */
  lastFailureTime?: number;

  /** Timestamp when the circuit should attempt to transition to half-open */
  nextRetryTime?: number;

  /** Timestamp when the circuit entered half-open state */
  halfOpenStartTime?: number;
}

// =============================================================================
// CIRCUIT BREAKER CONFIGURATION
// =============================================================================

/**
 * Configuration options for the circuit breaker.
 *
 * These options control when the circuit opens, closes, and how it monitors
 * endpoint health over time.
 *
 * @example
 * ```typescript
 * import type { CircuitBreakerConfig } from '../types/api';
 *
 * const config: CircuitBreakerConfig = {
 *   failureThreshold: 5,
 *   successThreshold: 3,
 *   timeout: 30000, // 30 seconds
 *   monitoringPeriod: 60000, // 1 minute
 *   minimumRequestVolume: 10,
 *   enabled: true,
 * };
 * ```
 */
export interface CircuitBreakerConfig {
  /**
   * Number of failures required to open the circuit.
   * Once this threshold is reached within the monitoring period, the circuit opens.
   */
  failureThreshold: number;

  /**
   * Number of consecutive successes required to close the circuit from half-open state.
   * This ensures the service has truly recovered before resuming normal traffic.
   */
  successThreshold: number;

  /**
   * Time in milliseconds before attempting to close an open circuit.
   * After this timeout, the circuit transitions to half-open to test recovery.
   */
  timeout: number;

  /**
   * Time window in milliseconds for counting failures.
   * Only failures within this window are considered when deciding to open the circuit.
   */
  monitoringPeriod: number;

  /**
   * Minimum number of requests before the circuit can open.
   * This prevents the circuit from opening due to a small number of failures.
   */
  minimumRequestVolume: number;

  /** Whether the circuit breaker is enabled */
  enabled: boolean;
}

// =============================================================================
// CIRCUIT BREAKER STATISTICS
// =============================================================================

/**
 * Comprehensive statistics for a circuit breaker endpoint.
 *
 * This interface provides detailed metrics about the circuit breaker's
 * behavior and the endpoint's health over time.
 *
 * @example
 * ```typescript
 * import type { CircuitBreakerStats } from '../types/api';
 *
 * function logStats(stats: CircuitBreakerStats): void {
 *   console.log(`Endpoint: ${stats.endpoint}`);
 *   console.log(`State: ${stats.state}`);
 *   console.log(`Failure rate: ${(stats.failureCount / stats.requestCount * 100).toFixed(1)}%`);
 *   console.log(`Circuit has tripped ${stats.tripCount} times`);
 * }
 * ```
 */
export interface CircuitBreakerStats {
  /** The endpoint being monitored */
  endpoint: string;

  /** Current circuit state */
  state: CircuitState;

  /** Total number of failures recorded */
  failureCount: number;

  /** Total number of successes recorded */
  successCount: number;

  /** Total number of requests made */
  requestCount: number;

  /** Timestamp of the last failure */
  lastFailureTime?: number;

  /** Timestamp of the last success */
  lastSuccessTime?: number;

  /** Timestamp when the circuit will next attempt recovery */
  nextRetryTime?: number;

  /** Timestamp when half-open testing started */
  halfOpenStartTime?: number;

  /** Number of times the circuit has tripped (opened) */
  tripCount: number;

  /** Average response time for successful requests */
  averageResponseTime?: number;
}

// =============================================================================
// CIRCUIT BREAKER INTERFACE
// =============================================================================

/**
 * Interface for the circuit breaker component.
 *
 * This interface defines all the methods available for interacting with
 * a circuit breaker instance, including checking state, recording results,
 * and manual control.
 *
 * @example
 * ```typescript
 * import type { CircuitBreaker } from '../types/api';
 *
 * function makeRequest(
 *   circuitBreaker: CircuitBreaker,
 *   endpoint: string
 * ): Promise<Response> {
 *   if (!circuitBreaker.shouldAllow(endpoint)) {
 *     throw new Error('Circuit is open, request blocked');
 *   }
 *
 *   try {
 *     const response = await fetch(endpoint);
 *     circuitBreaker.recordSuccess(endpoint);
 *     return response;
 *   } catch (error) {
 *     circuitBreaker.recordFailure(endpoint);
 *     throw error;
 *   }
 * }
 * ```
 */
export interface ICircuitBreaker {
  /**
   * Determines if a request should be allowed for the given endpoint.
   * Returns false if the circuit is open and the timeout hasn't elapsed.
   */
  shouldAllow(endpoint: string): boolean;

  /**
   * Records a successful request for the endpoint.
   * In half-open state, this helps close the circuit when success threshold is met.
   */
  recordSuccess(endpoint: string): void;

  /**
   * Records a failed request for the endpoint.
   * May cause the circuit to open if failure threshold is exceeded.
   */
  recordFailure(endpoint: string): void;

  /**
   * Gets the current state of a circuit (CLOSED, OPEN, or HALF_OPEN).
   */
  getState(endpoint: string): CircuitState;

  /**
   * Gets the full circuit breaker state for an endpoint.
   */
  getCircuitState(endpoint: string): CircuitBreakerState;

  /**
   * Gets all circuit breaker states across all endpoints.
   */
  getAllCircuitStates(): Map<string, CircuitBreakerState>;

  /**
   * Manually resets a circuit to closed state.
   * Clears all failure counts and timing information.
   */
  resetCircuit(endpoint: string): void;

  /**
   * Manually forces a circuit to open state.
   * Useful for maintenance or known service issues.
   */
  forceOpen(endpoint: string): void;

  /**
   * Manually forces a circuit to close state.
   * Use with caution - may allow requests to a failing service.
   */
  forceClose(endpoint: string): void;

  /**
   * Gets comprehensive statistics for an endpoint.
   */
  getStats(endpoint: string): CircuitBreakerStats;

  /**
   * Adds a handler to receive circuit breaker state change notifications.
   */
  addNotificationHandler(handler: (event: CoreNotificationEvent) => void): void;

  /**
   * Removes a previously added notification handler.
   */
  removeNotificationHandler(
    handler: (event: CoreNotificationEvent) => void
  ): void;
}

// =============================================================================
// ENDPOINT HEALTH TYPES
// =============================================================================

/**
 * Health status categories for an endpoint.
 */
export type EndpointHealthStatus = 'healthy' | 'degraded' | 'unhealthy';

/**
 * Health assessment result for an endpoint based on circuit breaker stats.
 *
 * @example
 * ```typescript
 * import type { EndpointHealth } from '../types/api';
 * import { calculateEndpointHealth } from '../types/api';
 *
 * const health = calculateEndpointHealth(stats);
 * if (health.status === 'unhealthy') {
 *   console.warn('Endpoint issues:', health.reasons.join(', '));
 * }
 * ```
 */
export interface EndpointHealth {
  /** Health score from 0-100 */
  score: number;

  /** Overall health status */
  status: EndpointHealthStatus;

  /** Reasons for any health degradation */
  reasons: string[];
}

// =============================================================================
// CONFIGURATION DEFAULTS
// =============================================================================

/**
 * Default configuration values for the circuit breaker.
 *
 * These defaults provide a reasonable starting point for most applications:
 * - Opens after 5 failures within 2 minutes
 * - Requires 10 requests minimum before opening
 * - Waits 1 minute before testing recovery
 * - Requires 3 successes to close from half-open
 *
 * @example
 * ```typescript
 * import { DEFAULT_CIRCUIT_BREAKER_CONFIG } from '../types/api';
 *
 * // Use defaults with some overrides
 * const config = {
 *   ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
 *   failureThreshold: 10,
 *   timeout: 30000,
 * };
 * ```
 */
export const DEFAULT_CIRCUIT_BREAKER_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 5, // Open circuit after 5 failures
  successThreshold: 3, // Close circuit after 3 consecutive successes in half-open
  timeout: 60000, // 60 seconds before attempting to close circuit
  monitoringPeriod: 120000, // 2 minutes monitoring window
  minimumRequestVolume: 10, // Minimum 10 requests before circuit can open
  enabled: true,
};

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid CircuitState enum value.
 *
 * @example
 * ```typescript
 * import { isCircuitState, CircuitState } from '../types/api';
 *
 * const state = 'closed';
 * if (isCircuitState(state)) {
 *   // state is typed as CircuitState
 * }
 * ```
 */
export function isCircuitState(value: unknown): value is CircuitState {
  return (
    typeof value === 'string' &&
    Object.values(CircuitState).includes(value as CircuitState)
  );
}

/**
 * Type guard to check if a value is a valid CircuitBreakerState.
 *
 * @example
 * ```typescript
 * import { isCircuitBreakerState } from '../types/api';
 *
 * if (isCircuitBreakerState(data)) {
 *   console.log(`Endpoint ${data.endpoint} is ${data.state}`);
 * }
 * ```
 */
export function isCircuitBreakerState(
  value: unknown
): value is CircuitBreakerState {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const state = value as Record<string, unknown>;
  return (
    typeof state.endpoint === 'string' &&
    isCircuitState(state.state) &&
    typeof state.failureCount === 'number' &&
    typeof state.successCount === 'number'
  );
}

/**
 * Type guard to check if a value is a valid CircuitBreakerConfig.
 *
 * @example
 * ```typescript
 * import { isCircuitBreakerConfig } from '../types/api';
 *
 * if (isCircuitBreakerConfig(userConfig)) {
 *   createCircuitBreaker(userConfig);
 * }
 * ```
 */
export function isCircuitBreakerConfig(
  value: unknown
): value is CircuitBreakerConfig {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const config = value as Record<string, unknown>;
  return (
    typeof config.failureThreshold === 'number' &&
    typeof config.successThreshold === 'number' &&
    typeof config.timeout === 'number' &&
    typeof config.monitoringPeriod === 'number' &&
    typeof config.minimumRequestVolume === 'number' &&
    typeof config.enabled === 'boolean'
  );
}

/**
 * Type guard to check if a value is a valid CircuitBreakerStats.
 */
export function isCircuitBreakerStats(
  value: unknown
): value is CircuitBreakerStats {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const stats = value as Record<string, unknown>;
  return (
    typeof stats.endpoint === 'string' &&
    isCircuitState(stats.state) &&
    typeof stats.failureCount === 'number' &&
    typeof stats.successCount === 'number' &&
    typeof stats.requestCount === 'number' &&
    typeof stats.tripCount === 'number'
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Validates circuit breaker configuration and returns any errors.
 *
 * @param config - The configuration to validate
 * @returns Object with valid flag and array of error messages
 *
 * @example
 * ```typescript
 * import { validateCircuitBreakerConfig } from '../types/api';
 *
 * const { valid, errors } = validateCircuitBreakerConfig(config);
 * if (!valid) {
 *   console.error('Invalid config:', errors);
 * }
 * ```
 */
export function validateCircuitBreakerConfig(config: CircuitBreakerConfig): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (config.failureThreshold <= 0) {
    errors.push('failureThreshold must be > 0');
  }

  if (config.successThreshold <= 0) {
    errors.push('successThreshold must be > 0');
  }

  if (config.timeout <= 0) {
    errors.push('timeout must be > 0');
  }

  if (config.monitoringPeriod <= 0) {
    errors.push('monitoringPeriod must be > 0');
  }

  if (config.minimumRequestVolume <= 0) {
    errors.push('minimumRequestVolume must be > 0');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Creates a circuit breaker configuration with defaults for any missing values.
 *
 * @param partial - Partial configuration to merge with defaults
 * @returns Complete CircuitBreakerConfig
 *
 * @example
 * ```typescript
 * import { createCircuitBreakerConfig } from '../types/api';
 *
 * const config = createCircuitBreakerConfig({
 *   failureThreshold: 10,
 *   timeout: 30000,
 * });
 * // Other values use defaults
 * ```
 */
export function createCircuitBreakerConfig(
  partial: Partial<CircuitBreakerConfig> = {}
): CircuitBreakerConfig {
  return {
    ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
    ...partial,
  };
}

/**
 * Calculates health score for an endpoint based on circuit breaker statistics.
 *
 * The health score is a value from 0-100 based on:
 * - Current circuit state (open = 0, half-open = 50, closed = based on failure rate)
 * - Recent failure rate in closed state
 * - Number of times the circuit has tripped
 *
 * @param stats - Circuit breaker statistics for the endpoint
 * @returns Health assessment with score, status, and reasons
 *
 * @example
 * ```typescript
 * import { calculateEndpointHealth } from '../types/api';
 *
 * const health = calculateEndpointHealth(stats);
 * console.log(`Health: ${health.score}/100 (${health.status})`);
 * if (health.reasons.length > 0) {
 *   console.log('Issues:', health.reasons);
 * }
 * ```
 */
export function calculateEndpointHealth(
  stats: CircuitBreakerStats
): EndpointHealth {
  const reasons: string[] = [];
  let score = 100;

  // Penalize based on state
  switch (stats.state) {
    case CircuitState.OPEN:
      score = 0;
      reasons.push('Circuit is open');
      break;
    case CircuitState.HALF_OPEN:
      score = 50;
      reasons.push('Circuit is in half-open state');
      break;
    case CircuitState.CLOSED:
      // Calculate based on recent failures
      if (stats.requestCount > 0) {
        const failureRate = (stats.failureCount / stats.requestCount) * 100;
        score = Math.max(0, 100 - failureRate);

        if (failureRate > 10) {
          reasons.push(`High failure rate: ${failureRate.toFixed(1)}%`);
        }
      }
      break;
  }

  // Penalize frequent trips
  if (stats.tripCount > 5) {
    score = Math.max(0, score - stats.tripCount * 5);
    reasons.push(`Frequent circuit trips: ${stats.tripCount}`);
  }

  const status: EndpointHealthStatus =
    score >= 80 ? 'healthy' : score >= 50 ? 'degraded' : 'unhealthy';

  return { score, status, reasons };
}
